import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, X, Loader2, CheckCircle2 } from "lucide-react";
import { reportInaccuracy } from "@/lib/api";
import { toast } from "sonner";

const KINDS = [
  { id: "factual",  label: "Wrong facts / doesn't match the book" },
  { id: "misalign", label: "Doesn't cover what the chapter actually teaches" },
  { id: "quiz",     label: "A quiz question has the wrong answer" },
  { id: "typos",    label: "Typos or garbled text" },
  { id: "other",    label: "Something else" },
];

export const ReportInaccuracyDialog = ({ open, onClose, lectureId, quizIndex = null }) => {
  const [kind, setKind] = useState(quizIndex != null ? "quiz" : "factual");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await reportInaccuracy(lectureId, {
        kind,
        note: note.trim().slice(0, 800),
        quiz_index: quizIndex != null ? quizIndex : null,
      });
      setDone(true);
      toast.success("Thanks — our team will review it.");
      setTimeout(() => {
        setDone(false);
        setNote("");
        onClose?.();
      }, 1400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit report. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-50 grid place-items-center bg-black/50 backdrop-blur-sm p-4"
          data-testid="report-inaccuracy-dialog"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-3xl border border-border bg-card shadow-2xl overflow-hidden"
          >
            <div className="flex items-center justify-between p-5 border-b border-border">
              <div className="flex items-center gap-2.5">
                <span className="grid place-items-center h-9 w-9 rounded-xl bg-accent/15 text-accent">
                  <AlertTriangle className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-serif text-lg font-bold leading-tight">Report an inaccuracy</p>
                  <p className="text-[11px] text-muted-foreground">Help us keep Lectura textbook-faithful.</p>
                </div>
              </div>
              <button
                data-testid="report-close-btn"
                onClick={onClose}
                className="grid place-items-center h-8 w-8 rounded-full hover:bg-secondary transition"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {done ? (
              <div className="p-8 text-center" data-testid="report-success">
                <span className="grid place-items-center h-14 w-14 mx-auto rounded-2xl bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                  <CheckCircle2 className="h-7 w-7" />
                </span>
                <p className="mt-3 font-serif text-xl font-bold">Report sent</p>
                <p className="mt-1 text-sm text-muted-foreground">Thank you — our team will take a look.</p>
              </div>
            ) : (
              <div className="p-5 space-y-4">
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
                    What's wrong?
                  </label>
                  <div className="mt-2 space-y-1.5">
                    {KINDS.map((k) => (
                      <label
                        key={k.id}
                        data-testid={`report-kind-${k.id}`}
                        className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 cursor-pointer transition ${
                          kind === k.id ? "border-accent bg-accent/5" : "border-border hover:border-primary/40"
                        }`}
                      >
                        <input
                          type="radio"
                          name="report-kind"
                          value={k.id}
                          checked={kind === k.id}
                          onChange={() => setKind(k.id)}
                          className="accent-accent h-4 w-4"
                        />
                        <span className="text-sm font-medium">{k.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
                    Tell us more <span className="opacity-60 font-normal normal-case tracking-normal">(optional)</span>
                  </label>
                  <textarea
                    data-testid="report-note-input"
                    value={note}
                    onChange={(e) => setNote(e.target.value.slice(0, 800))}
                    rows={4}
                    maxLength={800}
                    placeholder="Which section, question or line? What should it say instead?"
                    className="mt-1.5 w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring/60 transition resize-y"
                  />
                  <p className="mt-1 text-[10px] text-muted-foreground text-right tabular-nums">
                    {note.length} / 800
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    data-testid="report-cancel-btn"
                    onClick={onClose}
                    className="flex-1 rounded-full border border-border py-2.5 text-sm font-semibold hover:border-primary/40 transition"
                  >
                    Cancel
                  </button>
                  <button
                    data-testid="report-submit-btn"
                    onClick={submit}
                    disabled={saving}
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-accent text-accent-foreground py-2.5 text-sm font-semibold disabled:opacity-60 hover:opacity-90 transition"
                  >
                    {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : "Send report"}
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
